import { useCallback, useEffect, useMemo, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import { useTranslation } from 'react-i18next';

import {
  PnlExportFileDocument,
  PnlExportFileQuery,
  PnlExportFileQueryVariables,
  PnlExportGroupType,
  PnlExportRunType,
  useOrderPnlExportMutation,
  usePnlExportGroupsQuery,
  usePnlExportRunsQuery,
} from 'gqlhooks';
import {
  PNL_FILE_UNAVAILABLE_KEY,
  PNL_UNKNOWN_ERROR_KEY,
  PnlFileFormat,
  base64ToBytes,
  hasActivePnlRuns,
  pnlErrorKey,
  pnlLanguage,
  saveBytesAsFile,
  validatePnlPeriod,
} from 'core/utils/pnlExport';

/**
 * Выгрузка данных для P&L (ТЗ 0075) — доменные хуки.
 *
 * Один набор на оба места: страницу `/finances/pnl` и кнопку на вкладке
 * «Финансы» карточки группы. Заказы нормализуются в кеше Apollo по `id`,
 * поэтому опрос в одном месте обновляет строку и в другом.
 */

export type IPnlExportRun = Pick<
  PnlExportRunType,
  | 'id'
  | 'status'
  | 'fileFormat'
  | 'language'
  | 'monthFrom'
  | 'monthTo'
  | 'groupId'
  | 'groupName'
  | 'fileName'
  | 'size'
  | 'stats'
  | 'error'
  | 'createdByName'
  | 'createdAt'
  | 'finishedAt'
  | 'isDownloadable'
  | 'isFilePurged'
>;

/** Как часто перечитывать список, пока хоть один заказ собирается. */
export const PNL_POLL_INTERVAL_MS = 3000;

interface IUsePnlExportRunsOptions {
  groupId?: string | null;
  limit?: number;
}

/**
 * Последние заказы (все или одной группы). Опрос включается ТОЛЬКО пока есть
 * `pending` / `running` и выключается, как только всё дособиралось, — иначе
 * открытая вкладка стучала бы на сервер каждые три секунды весь день.
 */
export const usePnlExportRuns = ({
  groupId,
  limit,
}: IUsePnlExportRunsOptions = {}) => {
  const {
    data,
    loading,
    error,
    refetch,
    startPolling,
    stopPolling,
  } = usePnlExportRunsQuery({
    variables: { groupId: groupId || null, limit: limit ?? null },
    fetchPolicy: 'cache-and-network',
  });

  const runs: IPnlExportRun[] = data?.pnlExportRuns ?? [];
  const active = hasActivePnlRuns(runs);

  useEffect(() => {
    if (!active) return undefined;
    startPolling(PNL_POLL_INTERVAL_MS);
    return () => stopPolling();
  }, [active, startPolling, stopPolling]);

  return { runs, loading, error, refetch, active };
};

export type IPnlExportGroup = Pick<
  PnlExportGroupType,
  'id' | 'name' | 'courseName' | 'startDate'
>;

/**
 * Группы для селектора страницы `/finances/pnl`.
 *
 * Только `pnlExportGroups`: обычные запросы групп FinanceManager не открыты.
 * В панели карточки группы не вызывается — там группа уже известна.
 */
export const usePnlExportGroups = () => {
  const { data, loading, error } = usePnlExportGroupsQuery({
    fetchPolicy: 'cache-and-network',
  });
  const groups: IPnlExportGroup[] = useMemo(() => data?.pnlExportGroups ?? [], [
    data,
  ]);
  return { groups, loading, error };
};

export interface IPnlExportRequest {
  monthFrom: string;
  monthTo: string;
  fileFormat: PnlFileFormat;
  groupId?: string | null;
}

export interface IPnlExportRequestResult {
  success: boolean;
  /** Ключ перевода; сообщение сервера — запасной хвост для незнакомого кода. */
  errorKey?: string;
  errorMessage?: string;
  run?: IPnlExportRun | null;
}

/**
 * Заказать выгрузку. Язык подписей колонок в файле — текущий язык интерфейса
 * (`ru` / `pl`, всё прочее — `ru`).
 */
export const useRequestPnlExport = () => {
  const { i18n } = useTranslation();
  const [orderRun] = useOrderPnlExportMutation();
  const [pending, setPending] = useState(false);

  const request = useCallback(
    async ({
      monthFrom,
      monthTo,
      fileFormat,
      groupId,
    }: IPnlExportRequest): Promise<IPnlExportRequestResult> => {
      const invalid = validatePnlPeriod(monthFrom, monthTo);
      if (invalid) return { success: false, errorKey: pnlErrorKey(invalid) };

      setPending(true);
      try {
        const result = await orderRun({
          variables: {
            monthFrom,
            monthTo,
            fileFormat,
            language: pnlLanguage(i18n.language),
            groupId: groupId || null,
          },
        });
        const payload = result.data?.requestPnlExport;
        if (payload?.success && payload.run) {
          return { success: true, run: payload.run };
        }
        const first = (payload?.customErrors ?? []).find(Boolean);
        return {
          success: false,
          errorKey: pnlErrorKey(first?.code),
          errorMessage: first?.message ?? undefined,
        };
      } catch (e) {
        return {
          success: false,
          errorKey: PNL_UNKNOWN_ERROR_KEY,
          errorMessage: (e as Error).message,
        };
      } finally {
        setPending(false);
      }
    },
    [orderRun, i18n.language],
  );

  return { request, pending };
};

/**
 * Скачать готовый файл.
 *
 * 🔴 `no-cache`, а не `network-only`: `network-only` тоже пишет ответ в кеш,
 * и мегабайты base64 оседали бы в ROOT_QUERY на всю сессию. Файл берётся
 * ПО КЛИКУ и в кеш не попадает вовсе.
 */
export const usePnlExportDownload = () => {
  const client = useApolloClient();
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const download = useCallback(
    async (runId: string): Promise<IPnlExportRequestResult> => {
      setDownloadingId(runId);
      try {
        const result = await client.query<
          PnlExportFileQuery,
          PnlExportFileQueryVariables
        >({
          query: PnlExportFileDocument,
          variables: { id: runId },
          fetchPolicy: 'no-cache',
        });
        const file = result.data?.pnlExportFile;
        if (!file) {
          return { success: false, errorKey: PNL_FILE_UNAVAILABLE_KEY };
        }
        saveBytesAsFile(
          base64ToBytes(file.contentBase64),
          file.contentType,
          file.fileName,
        );
        return { success: true };
      } catch (e) {
        return {
          success: false,
          errorKey: PNL_FILE_UNAVAILABLE_KEY,
          errorMessage: (e as Error).message,
        };
      } finally {
        setDownloadingId(null);
      }
    },
    [client],
  );

  return { download, downloadingId };
};
